import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FormProvider, useForm, yupResolver } from 'FrontRoyalReactHookForm';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import {
    getHasOfferOrIncludedOrCompleted,
    getIsCurrentOrHasCompletedProgram,
    getStudentEmailAddressForLastActiveAoiRecord,
    userActionsApi,
    type CurrentUser,
} from 'Users';
import { targetBrand, formattedBrandName } from 'AppBranding';
import { navigationHelper } from 'NavigationHelpers';
import { Button } from 'Button';
import { InlineLink } from 'InlineLink';
import { Input } from 'Input';
import { Text } from 'Text';
import { Tooltip } from 'Tooltip';
import { showToast } from 'Toast';
import { Divider } from '../../settingsShared/Divider';
import { type ForwardingAddressFormValues } from '../editAccount.types';

const MAX_LENGTH = 255;

const hasClipboardAccess = () => !!navigator.clipboard && window.isSecureContext;

// A value with a copy-to-clipboard icon (shown only when clipboard access is available), mirroring
// the old `.copyable` rows. A tooltip on the icon flips from "Copy" to "Copied!" for 2s after a copy
// (matching the old uib-tooltip feedback).
function CopyableValue({ value }: { value: string }) {
    const { t } = useTranslation('back_royal');
    const [copied, setCopied] = useState(false);

    if (!hasClipboardAccess()) return <Text as="div">{value}</Text>;

    const copy = async () => {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="flex items-center gap-2">
            <Text as="div">{value}</Text>
            <Tooltip content={copied ? t('settings.edit_account.copied') : t('settings.edit_account.copy')}>
                <Button
                    isIconOnly
                    variant="light"
                    color="slate-grey"
                    height={8}
                    aria-label={t('settings.edit_account.copy')}
                    onPress={copy}
                >
                    <FontAwesomeIcon icon={faCopy} className="text-slate-grey" />
                </Button>
            </Tooltip>
        </div>
    );
}

// Student-email forwarding address block + its save form. Split out from StudentEmailSection so the
// form state lives with the form (and to keep each component under the max-lines limit). Only
// rendered when the user has a mailgun-routed student email.
type StudentEmailAddress = NonNullable<ReturnType<typeof getStudentEmailAddressForLastActiveAoiRecord>>;

function StudentEmailForwarding({ emailAddress }: { emailAddress: StudentEmailAddress }) {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const { loadFaqUrl } = navigationHelper();
    const [updateCurrentUser] = userActionsApi.useUpdateCurrentUserMutation();
    const [submitting, setSubmitting] = useState(false);

    const brand = targetBrand(emailAddress.domain);
    const brandName = brand ? formattedBrandName('short', brand) : '';

    const schema = Yup.object({
        forwarding_address: Yup.string().trim().required().email().max(MAX_LENGTH),
    });
    const methods = useForm<ForwardingAddressFormValues>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: { forwarding_address: emailAddress.forwardingAddress ?? '' },
    });
    const { isValid, errors } = methods.formState;

    const saveForwardingAddress = async ({ forwarding_address }: ForwardingAddressFormValues) => {
        setSubmitting(true);
        try {
            await updateCurrentUser({
                user: { id: currentUser.id, student_email_addresses: [{ id: emailAddress.id, forwarding_address }] },
            }).unwrap();
            showToast({ message: t('settings.edit_account.account_update_success'), type: 'success' });
        } catch {
            showToast({ message: t('settings.edit_account.account_update_failure'), type: 'danger' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col gap-4 py-6">
            <Text as="h2" color="black" weight="semibold" className="text-base">
                {t('settings.edit_account.email_forwarding')}
            </Text>
            <Text as="p" variant="small">
                <Trans
                    t={t}
                    i18nKey={`settings.edit_account.email_forwarding_desc_${brand}`}
                    values={{ brandName }}
                    components={{
                        faq: (
                            <InlineLink
                                className="text-xs"
                                onPress={() => loadFaqUrl('/help/student-discounts', '_blank')}
                            />
                        ),
                    }}
                />
            </Text>

            <div>
                <Text as="div" weight="semibold" className="text-sm">
                    {t('settings.edit_account.your_student_email_address', { brandName })}
                </Text>
                <CopyableValue value={emailAddress.address} />
            </div>

            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(saveForwardingAddress)} className="flex max-w-md flex-col gap-4">
                    <Text as="div" variant="small">
                        {t('settings.edit_account.forwarding_address_desc', { brandName })}
                    </Text>
                    <Input
                        name="forwarding_address"
                        label={t('settings.edit_account.forwarding_address')}
                        type="email"
                        isInvalid={!!errors.forwarding_address}
                    />
                    <Button type="submit" color="primary" height={9} fullWidth isDisabled={!isValid || submitting}>
                        {t('settings.edit_account.save_change')}
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
}

// Per-block visibility for the student-email section. Exported as the single source of truth so the
// EditAccount container can decide whether to slot the section (and its leading divider) in without
// duplicating — and risking drift from — the gating logic here.
export function getStudentEmailSectionState(currentUser: CurrentUser) {
    const showStudentId = !!currentUser.studentId && getHasOfferOrIncludedOrCompleted(currentUser);
    const emailAddress = getStudentEmailAddressForLastActiveAoiRecord(currentUser);
    const showStudentEmail = !!emailAddress?.mailgunRouteId && getIsCurrentOrHasCompletedProgram(currentUser);
    return { showStudentId, showStudentEmail, emailAddress };
}

// Student ID (copyable) + student-email forwarding address. Each block is independently gated,
// matching the old edit-account directive.
export function StudentEmailSection() {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const { showStudentId, showStudentEmail, emailAddress } = getStudentEmailSectionState(currentUser);

    if (!showStudentId && !showStudentEmail) return null;

    return (
        <>
            {showStudentId ? (
                <div className="flex flex-col gap-1 py-6">
                    <Text as="h2" color="black" weight="semibold" className="text-base">
                        {t('settings.edit_account.student_id')}
                    </Text>
                    <CopyableValue value={currentUser.studentId as string} />
                </div>
            ) : null}

            {showStudentEmail && emailAddress ? (
                <>
                    {showStudentId ? <Divider /> : null}
                    <StudentEmailForwarding emailAddress={emailAddress} />
                </>
            ) : null}
        </>
    );
}
